import React, { useEffect, useRef, useMemo } from "react";
import * as d3 from "d3";
import { fetchAPI } from "../../config/api";
import { 
  processSprayChartData, 
  computeAggregates, 
  getHeatColor 
} from "../../utils/sprayChartUtils";

const CONTAINER_HEIGHT = 280;

const VB_WIDTH = 400;
const VB_HEIGHT = 340;

export const SprayChart = ({
  playerId,
  year,
  division,
  prefetchedData = null,
}) => {
  const svgRef = useRef();
  const [playerData, setPlayerData] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(!prefetchedData);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (prefetchedData) {
      const processed = processSprayChartData({ ...prefetchedData, year });
      setPlayerData(processed);
      setEvents(Array.isArray(prefetchedData.events) ? prefetchedData.events : []);
      setLoading(false);
      return;
    }

    const fetchPlayerData = async () => {
      setLoading(true);
      try {
        const data = await fetchAPI(
          `/spraychart_data/${playerId}?year=${year}&division=${division}`
        );
        const processed = processSprayChartData({ ...data, year });
        setPlayerData(processed);
        setEvents(Array.isArray(data.events) ? data.events : []);
        setError(null);
      } catch (err) {
        console.error("Error fetching player data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayerData();
  }, [division, playerId, year, prefetchedData]);

  const aggregates = useMemo(() => computeAggregates(events), [events]);

  useEffect(() => {
    if (!svgRef.current || !playerData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Background
    svg.append("rect")
      .attr("width", VB_WIDTH)
      .attr("height", VB_HEIGHT)
      .attr("fill", "#F5FBFF");

    // Header
    const headerHeight = 55;
    const headerMargin = 12;
    
    svg.append("rect")
      .attr("x", headerMargin)
      .attr("y", 8)
      .attr("width", VB_WIDTH - headerMargin * 2)
      .attr("height", headerHeight)
      .attr("fill", "#E1F5FE")
      .attr("rx", 6);

    svg.append("text")
      .attr("x", headerMargin + 12)
      .attr("y", 32)
      .attr("font-weight", "bold")
      .attr("font-size", "16px")
      .attr("fill", "#1a365d")
      .text(playerData.player_name);

    svg.append("text")
      .attr("x", headerMargin + 12)
      .attr("y", 50)
      .attr("font-size", "12px")
      .attr("fill", "#4a5568")
      .text(playerData.playerInfo);

    // Field - positioned to fill remaining space
    const fieldTop = headerHeight + 25;
    const fieldCenterX = VB_WIDTH / 2;
    const fieldCenterY = VB_HEIGHT - 5;
    const arcRadius = (VB_HEIGHT - fieldTop - 30) * 0.9;

    const field = svg.append("g").attr("transform", `translate(${fieldCenterX}, ${fieldCenterY})`);

    drawOutfield(field, arcRadius, aggregates.outfieldZones);
    drawInfield(field, arcRadius * 0.45, aggregates.infieldZones);
  }, [playerData, aggregates]);

  if (loading) {
    return (
      <div className="flex justify-center items-center" style={{ height: CONTAINER_HEIGHT }}>
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !playerData) {
    return (
      <div className="flex items-center justify-center" style={{ height: CONTAINER_HEIGHT }}>
        <div className="text-center text-red-500 text-sm">
          <div className="font-bold mb-1">Error loading data</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: CONTAINER_HEIGHT }}>
      <svg 
        ref={svgRef} 
        viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", width: "100%", height: "100%" }} 
      />
    </div>
  );
};

const drawOutfield = (field, arcRadius, zoneData) => {
  const pie = d3.pie().startAngle(-0.785398).endAngle(0.785398).value(() => 1).sort(null);
  const arc = d3.arc().innerRadius(0).outerRadius(arcRadius);
  const slices = pie([1, 1, 1]);

  slices.forEach((slice, i) => {
    const zone = zoneData[i] || {};
    const percentage = zone.percentage || 0;
    const hrCount = zone.hrCount || 0;

    field.append("path")
      .attr("d", arc(slice))
      .attr("fill", getHeatColor(percentage, 'outfield'))
      .attr("stroke", "#1a202c")
      .attr("stroke-width", 1.5);

    const angle = (slice.startAngle + slice.endAngle) / 2;
    const textR = arcRadius * 0.65;

    field.append("text")
      .attr("x", Math.sin(angle) * textR)
      .attr("y", -Math.cos(angle) * textR)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-weight", "bold")
      .attr("font-size", "18px")
      .attr("fill", "#1a202c")
      .text(`${percentage}%`);

    // HR circles
    const hrRadius = arcRadius * 1.12;
    const hrX = Math.sin(angle) * hrRadius;
    const hrY = -Math.cos(angle) * hrRadius;

    field.append("circle")
      .attr("cx", hrX)
      .attr("cy", hrY)
      .attr("r", 14)
      .attr("fill", "#fff")
      .attr("stroke", "#2b6cb0")
      .attr("stroke-width", 2);

    field.append("text")
      .attr("x", hrX)
      .attr("y", hrY)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-weight", "bold")
      .attr("font-size", "11px")
      .attr("fill", "#2b6cb0")
      .text(hrCount);
  });
};

const drawInfield = (field, infieldRadius, zoneData) => {
  const pie = d3.pie().startAngle(-0.785398).endAngle(0.785398).value(() => 1).sort(null);
  const arc = d3.arc().innerRadius(0).outerRadius(infieldRadius);
  const slices = pie([1, 1, 1, 1, 1]);

  slices.forEach((slice, i) => {
    const zone = zoneData[i] || {};
    const percentage = zone.percentage || 0;

    field.append("path")
      .attr("d", arc(slice))
      .attr("fill", getHeatColor(percentage, 'infield'))
      .attr("stroke", "#1a202c")
      .attr("stroke-width", 1);

    if (percentage > 0) {
      const angle = (slice.startAngle + slice.endAngle) / 2;
      const textR = infieldRadius * 0.55;
      
      field.append("text")
        .attr("x", Math.sin(angle) * textR)
        .attr("y", -Math.cos(angle) * textR)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .attr("font-weight", "bold")
        .attr("font-size", "9px")
        .attr("fill", "#1a202c")
        .text(`${percentage}%`);
    }
  });
};
